import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Box,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { FamilyMember } from "../utils/familyTreeUtils";
import { useAuth, db, storage } from "../firebase";

interface PersonDetailCardProps {
  member: FamilyMember;
  onChange: (updatedMember: FamilyMember) => Promise<void>;
}

const PersonDetailCard: React.FC<PersonDetailCardProps> = ({
  member,
  onChange,
}) => {
  const { user } = useAuth();
  const userId = user?.uid;

  const [name, setName] = useState(member?.name || "");
  const [birthdate, setBirthdate] = useState(member?.birthdate || "");
  const [deathdate, setDeathdate] = useState(member?.deathdate || "");
  const [image, setImage] = useState(member?.imageMetadata || "");
  const [isEdited, setIsEdited] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(member?.name || "");
    setBirthdate(member?.birthdate || "");
    setDeathdate(member?.deathdate || "");
    setImage(member?.imageMetadata || "");
    setIsEdited(false);
  }, [member]);

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => {
    setter(value);
    setIsEdited(true);
  };

  const handleSave = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Call the onChange callback to let the parent component handle the Firestore update
      // This ensures the array structure is maintained
      setIsEdited(false);
      await onChange({
        ...member,
        name,
        birthdate,
        deathdate,
        imageMetadata: image,
      });
    } catch (error) {
      console.error("Error saving member: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    memberId: string
  ) => {
    if (!userId) return;
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const storageRef = ref(storage, `users/${userId}/images/${memberId}.jpg`);
      setLoading(true);
      try {
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        setImage(downloadURL);
        setIsEdited(true);
        // Image updates will be handled by the parent component via onChange
        await onChange({
          ...member,
          name,
          birthdate,
          deathdate,
          imageMetadata: downloadURL,
        });
      } catch (error) {
        console.error("Error uploading image: ", error);
      } finally {
        setLoading(false);
      }
    }
  };

  if (!member) return null;

  // Create a dynamic title based on the person's name or a default
  const cardTitle = name || member.name || "Family Member Details";

  return (
    <Card elevation={5} sx={{ maxWidth: 800, overflow: "hidden", padding: 0 }}>
      {/* Custom Header Banner - Full Width */}
      <Box
        sx={{
          backgroundColor: "#8B4A6B", // Maroon color
          color: "white",
          padding: "20px 24px",
          margin: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          fontWeight="bold"
          color="white"
          sx={{ margin: 0, marginBottom: "4px" }}
        >
          {cardTitle}
        </Typography>
        <Typography
          variant="body2"
          color="white"
          sx={{ opacity: 0.9, margin: 0 }}
        >
          Edit family member information and upload photos
        </Typography>
      </Box>
      <CardContent sx={{ display: "flex" }}>
        {/* Avatar and upload */}
        <Box sx={{ marginRight: 2 }}>
          <Avatar src={image} sx={{ width: 240, height: 240 }}>
            {loading && <CircularProgress />}
          </Avatar>
          <input
            accept="image/*"
            id="icon-button-file"
            type="file"
            hidden
            onChange={(e) => handleImageChange(e, member.id)}
          />
          <label htmlFor="icon-button-file">
            <IconButton component="span" disabled={loading}>
              <PhotoCamera />
            </IconButton>
          </label>
        </Box>
        {/* Text fields */}
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => handleFieldChange(setName, e.target.value)}
            fullWidth
            margin="dense"
            disabled={loading}
          />
          <TextField
            label="Birthdate"
            type="date"
            value={birthdate}
            onChange={(e) => handleFieldChange(setBirthdate, e.target.value)}
            fullWidth
            margin="dense"
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Death Date (leave empty if living)"
            type="date"
            value={deathdate}
            onChange={(e) => handleFieldChange(setDeathdate, e.target.value)}
            fullWidth
            margin="dense"
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </CardContent>
      <CardActions>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isEdited || loading}
        >
          Save
        </Button>
      </CardActions>
    </Card>
  );
};

export default PersonDetailCard;
